import { OutreachApproval, OutreachLevel, OutreachReachedKind, type ShiftRequest } from "@/utils/models";
import { ALL_ALLOWED, type DemoState, initialAgencyOf } from "./demoOutreachPlan";
import type { RosterSplit } from "./demoOutreachRoster";
import { type DemoContext, seedOutreach, shiftTypeOf } from "./demoOutreachSeed";

/** DEMO ONLY (APP-933): a request's demo state as first seeded, before the user does anything. */
export const initialStateOf = (request: ShiftRequest, ctx: DemoContext, split: RosterSplit): DemoState => {
	const seeded = seedOutreach(request, ctx, split);
	const start = request.message.shift_start.setZone(ctx.timezone);
	// DEFAULT: a replace request has no `needed_nurses`; it always replaces one person.
	const neededCount = "needed_nurses" in request ? request.needed_nurses : 1;
	const approved = seeded.signUps.filter((signUp) => signUp.approval === OutreachApproval.ByManager || signUp.approval === OutreachApproval.Automatic).length;
	const agencyAt = seeded.starts[OutreachLevel.Agency];
	const agency = initialAgencyOf(neededCount, null);
	const posted = ctx.now >= agencyAt && neededCount - approved >= agency.minOpenSpots ? agencyAt : null;
	return {
		outreach: {
			requestId: request.id,
			shift: {
				start,
				end: request.message.shift_end.setZone(ctx.timezone),
				shiftType: shiftTypeOf(request),
				roles: request.message.roles,
				// DEFAULT: `job_id`/`sub_role` are absent on a request with no job; the model says so with `null`.
				jobId: request.job_id ?? null,
				subRole: request.sub_role ?? null,
				neededCount,
			},
			paused: false,
			// Overtime offers already out reach people the filters left out, so they count among who fits.
			eligible:
				seeded.ranked.length + seeded.sentBatches.flatMap((batch) => batch.people).filter((entry) => entry.kind === OutreachReachedKind.Override).length,
			sentBatches: seeded.sentBatches,
			plannedBatches: seeded.plannedBatches,
			excludedGroups: seeded.excludedGroups,
			skipped: [],
			signUps: seeded.signUps,
		},
		automaticStarts: seeded.starts,
		override: null,
		allowed: ALL_ALLOWED,
		autoApprove: ctx.autoApprove,
		agency: { ...agency, postedAt: posted },
		skippedFrom: new Map(),
		sends: 0,
	};
};
